import {
    Component,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { finalize, switchMap, take, tap } from 'rxjs';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IDeactivateComponent } from '../../../authentication/i-deactivate/i-deactivate.component';
import { TranslateService } from '@ngx-translate/core';
import { LanguageCode } from 'src/app/navigation/menu/components/language-nav-menu/data/laguage-code';
import { ToastMessageService } from '../../../shared/services/toast-message.service';
import { nonNil, trimFormValues } from '../../../shared/utility';
import { OrganizationService } from '../../services/organization.service';
import { OrganizationDto } from '../../model/organization';
import { validationPatterns } from 'src/app/shared/validation-patterns';

interface ValidationRule {
    minLength: number;
    maxLength: number;
    pattern: string;
}

@Component({
    selector: 'app-organization-details-page',
    templateUrl: './organization-details-page.component.html',
    styleUrls: ['./organization-details-page.component.scss'],
    standalone: false,
})
export class OrganizationDetailsPageComponent
    implements OnInit, IDeactivateComponent, OnChanges
{
    //HTML imports
    protected readonly LanguageCode = LanguageCode;
    @Input() orgId: string;

    //Extra props outside form to extra security against changes
    // asign empty value to prevent flickering as form would be first filled with 'undefined'
    updating: boolean = false;
    isExperimentalUser: boolean;

    readonly TOAST_MESSAGE_DELAY = { delay: 2000 };

    protected readonly validationRules: Record<string, ValidationRule> = {
        name: {
            minLength: 1,
            maxLength: 30,
            pattern:
                validationPatterns.alphaNumericPointMinusUnderScoreBlankPattern,
        },
        // description: {
        //     minLength: 0,
        //     maxLength: 50,
        //     pattern:
        //         validationPatterns.alphaNumericPointMinusUnderScoreBlankPattern,
        // },
    };

    // Field configurations for template iteration
    protected readonly fields = [
        {
            id: 'name',
            labelKey:
                'organization.organizationArea.createOrganization.form.name',
            errorKey:
                'organization.organizationArea.createOrganization.form.errors',
        },
        // {
        //     id: 'description',
        //     labelKey:
        //         'organization.organizationArea.createOrganization.form.description',
        //     errorKey:
        //         'organization.organizationArea.createOrganization.form.errors',
        // },
    ];

    organizationDetailsForm: FormGroup;

    constructor(
        private organizationService: OrganizationService,
        private authenticationService: AuthenticationService,
        private translateService: TranslateService,
        private messageService: ToastMessageService
    ) {}
    ngOnChanges(changes: SimpleChanges): void {
        this.updateForm();
    }

    ngOnInit(): void {
        this.isExperimentalUser = this.authenticationService.isExperimental();
        // Build form controls from validation rules
        const formControls: Record<string, FormControl> = {};
        for (const [field, rule] of Object.entries(this.validationRules)) {
            formControls[field] = new FormControl('', [
                Validators.minLength(rule.minLength),
                Validators.maxLength(rule.maxLength),
                Validators.pattern(rule.pattern),
            ]);
        }

        this.organizationDetailsForm = new FormGroup(formControls);

        if (this.authenticationService.isLoggedIn()) {
            this.updateForm();
        } else {
            console.error('There is an internal error.');
            this.authenticationService.signOut();
        }
    }

    updateForm() {
        this.organizationService
            .getOrganization(this.orgId)
            .pipe(
                take(1),
                tap((org: OrganizationDto) => {
                    this.patchForm(org);
                })
            )
            .subscribe();
    }

    private patchForm(org: OrganizationDto) {
        this.organizationDetailsForm.patchValue({
            name: org?.name || '',
            description: org?.description || '',
        });

        // Mark form as pristine after loading initial values
        this.organizationDetailsForm.markAsPristine();
    }

    /**
     * Checks if there are unsaved changes in the form
     */
    hasUnsavedChanges(): boolean {
        return this.organizationDetailsForm?.dirty ?? false;
    }

    /**
     * Implementation of IDeactivateComponent interface
     * Shows confirmation dialog when navigating away with unsaved changes
     */
    canExit(): boolean {
        if (this.hasUnsavedChanges()) {
            return window.confirm(
                this.translateService.instant(
                    'organization.organizationArea.organizationDetails.unsavedChangesWarning'
                )
            );
        }
        return true;
    }

    /**
     * Handles browser beforeunload event to show warning when closing tab with unsaved changes
     */
    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: BeforeUnloadEvent): void {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
            event.returnValue = false;
        }
    }

    /**
     * Saves changes when the Save Changes button is clicked
     * Currently only handles language changes (stored in localStorage)
     * Other organizationDetails fields are read-only from Keycloak/user administration
     */
    saveChanges(): void {
        if (!this.hasUnsavedChanges()) {
            return;
        }

        // Trim whitespace from all string fields
        const formValues = trimFormValues(this.organizationDetailsForm.value);

        if (this.organizationDetailsForm.valid) {
            this.updating = true;
            this.organizationService
                .updateOrg(formValues, this.orgId)
                .pipe(
                    finalize(() => {
                        this.updating = false;
                    }),
                    switchMap(() =>
                        this.authenticationService.refreshUserAuth()
                    )
                )
                .subscribe({
                    next: (_) => {
                        this.messageService.showTranslatedSuccessMsg(
                            'organization.organizationArea.organizationDetails.saveChangesSucess',
                            this.TOAST_MESSAGE_DELAY
                        );
                        // Mark form as clean after changes are applied
                        this.organizationDetailsForm.markAsPristine();
                    },
                    error: (err) => {
                        if (err.status < 500) {
                            // backend denied update due to validation rules
                            this.messageService.showTranslatedErrorMsg(
                                'organization.organizationArea.organizationDetails.saveChangesFailure',
                                this.TOAST_MESSAGE_DELAY
                            );
                        } else {
                            this.messageService.showTranslatedErrorMsg(
                                'organization.organizationArea.organizationDetails.saveChangesError',
                                this.TOAST_MESSAGE_DELAY
                            );
                        }
                    },
                });
        }
    }

    /**
     * Placeholder for account deletion functionality.
     * TODO: Implement account deletion dialog in future ticket.
     */
    onDeleteOrganization(): void {
        console.log(
            'Delete organization clicked - functionality not yet implemented'
        );
    }

    focusFieldOnError(event: FocusEvent, fieldId: string): void {
        const organizationDetailsFromField =
            this.organizationDetailsForm?.get(fieldId);
        if (nonNil(organizationDetailsFromField)) {
            organizationDetailsFromField.setValue(
                organizationDetailsFromField.value.trim()
            );

            if (organizationDetailsFromField.invalid) {
                const target = event.target as HTMLInputElement;
                target.focus();
            }
        }
    }
}
